import * as React from 'react';
import { commands } from '../utils/skipLinkHotkeys';
import { getDocument } from 'office-ui-fabric-react/lib/Utilities';
import { IButton } from 'office-ui-fabric-react/lib/Button';
import { IsFocusVisibleClassName } from 'office-ui-fabric-react/lib/Utilities';
import { keydownHandler } from 'owa-hotkeys';
import { observer } from 'mobx-react';
import SkipLinkButton from './SkipLinkButton';

let styles = require('./SkipLinkControl.scss');
let classNames = require('classnames/bind').bind(styles);

const SkipLinkNameAttribute = 'data-skip-link-name';

/**
 * The state for the skip link control
 */
export interface SkipLinkControlState {
    visible: boolean;
}

export interface ISkipLinkControl {
    focus(): void;
}

/**
 * A control for parsing landmark regions on a page and creating skip links to make tabbable shortcut buttons to jump to those regions.
 */
@observer
export default class SkipLinkControl extends React.Component<{}, SkipLinkControlState>
    implements ISkipLinkControl {
    private buttonMap: IButton[] = [];
    private focusedButtonIndex: number;

    constructor(props: {}) {
        super(props);
        this.state = { visible: false };
    }

    render() {
        // unused reference to fix ts warning for unused method, keydownHandler binds to the component but ts doesn't understand it
        this.onKeyDown;

        // when the skip link control starts rendering, search the DOM to find all elements that are marked as skip links
        // the dom might have changed since mounting or last rendering if other skip link items were rendered, so this is necessary
        let itemLinkButtons: JSX.Element[] = [];

        let currentDoc: Document = getDocument();
        let skipLinkElements = currentDoc
            ? currentDoc.querySelectorAll('[' + SkipLinkNameAttribute + ']')
            : null;

        for (let i = 0; i < skipLinkElements.length; i++) {
            let item = skipLinkElements.item(i);
            let name = item.getAttribute(SkipLinkNameAttribute);

            if (item instanceof HTMLElement) {
                itemLinkButtons.push(
                    <SkipLinkButton
                        key={name}
                        name={name}
                        index={i}
                        item={item}
                        shouldAutoFocus={i == 0}
                        visible={this.state.visible}
                        onFocus={this.onButtonFocus}
                        componentRef={this.defaultButtonMapRefCallback}
                    />
                );
            }
        }

        return (
            <div
                className={classNames(
                    styles.wrapper,
                    this.state.visible ? styles.visible : styles.invisible,
                    // manually manage Fabric focus styling to simulate tabbing when cycling through skip links using ctrl+f6
                    this.state.visible && IsFocusVisibleClassName
                )}
                onBlur={this.onBlur}>
                {itemLinkButtons}
            </div>
        );
    }

    public focus() {
        let button = this.buttonMap[0];
        if (button) {
            button.focus();
        }
    }

    private defaultButtonMapRefCallback = (button: IButton, index: number) => {
        this.buttonMap[index] = button;
    };

    private onBlur = (evt: React.FocusEvent<HTMLElement>) => {
        if (
            !(
                evt.currentTarget &&
                evt.relatedTarget &&
                evt.currentTarget.contains &&
                (evt.relatedTarget as HTMLElement).contains &&
                evt.currentTarget.contains(evt.relatedTarget as HTMLElement)
            )
        ) {
            // if the relatedTarget is one of the other buttons, no need to set state to invisible
            this.setState({
                visible: false,
            });
        }
    };

    private onButtonFocus = (index: number) => {
        this.focusedButtonIndex = index;
        this.setState({
            visible: true,
        });
    };

    @keydownHandler(commands.focusSkipLinks, true /*stop propagation */, true /*prevent default*/)
    private onKeyDown() {
        // using ctrl+f6 from the skip link control should cycle through the skip links just like tab
        let index =
            this.focusedButtonIndex == this.buttonMap.length - 1 ? 0 : this.focusedButtonIndex + 1;
        let button = this.buttonMap[index];
        if (button) {
            button.focus();
        }
    }
}
